/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ChatMessage,
  SchemaDisplay,
  SchemaField,
  StatusType,
} from './onboardingTypes'
import './InitialApplicationChatAssistant.scss'

// AI Brain icon from AITool
import aiIcon from './ada.png'
import { useDispatch, useSelector } from 'react-redux'
import { getId } from '@adalo/utils'
import { getCurrentAppId, State } from 'ducks/editor/objects'
import { createApplicationAssistantActions } from 'ducks/createApplicationAssistant'
import { getApp } from 'ducks/apps'
import { AIAssistantSocketClient } from './socketClient'

type Action = 'change-database' | 'design-database' | 'other'
type ApplicationCreationStatus =
  | 'build_started'
  | 'creating_database'
  | 'creating_database_failed'
  | 'creating_application'
  | 'creating_application_failed'
  | 'saving_database'
  | 'saving_database_failed'
  | 'creating_dummy_data'
  | 'creating_dummy_data_failed'
  | 'generating_images'
  | 'creating_screens'
  | 'creating_screens_failed'
  | 'finished'

// AI Icon component using the brain image
const AIIcon = () => (
  <img
    src={aiIcon}
    alt="AI"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      overflow: 'hidden',
      borderRadius: '100%',
    }}
  />
)

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
)

const UserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const InitialApplicationChatAssistant = () => {
  const dispatch = useDispatch()
  const store = useSelector((store: State) => store)
  const state = useSelector((store: any) => store.createApplicationAssistant)
  // Chat state - persists even when chat is closed
  const {
    appState,
    loading: loadingStatus,
    messages,
    buildingApplicationStatus,
  } = state as {
    appState: {
      applicationName: string
      branding: {
        primary: string
        secondary: string
        background: string
        text: string
      }
    }
    loading: boolean
    buildingApplicationStatus: string | false
    messages: ChatMessage[]
  }
  const isLoading = loadingStatus || buildingApplicationStatus !== false
  const [inputValue, setInputValue] = useState('')
  const [showMessageChanges, setShowMessageChanges] = useState<string | null>(
    null
  )

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const getOrganizationId = () => {
    const appId = getCurrentAppId(store)

    if (appId === undefined || appId === null) {
      return undefined
    }

    const app = getApp(store, appId)

    return app?.Organization?.id
  }

  const addConversationMessages = useCallback(
    (newMessages: ChatMessage[]) => {
      dispatch({
        type: createApplicationAssistantActions.ADD_MESSAGES,
        payload: newMessages,
      })
    },
    [messages]
  )

  const setIsLoading = (value: boolean) => {
    dispatch({
      type: createApplicationAssistantActions.SET_LOADING,
      payload: value,
    })
  }

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when chat opens
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  // Add AI message with typing animation
  const addAIMessage = useCallback(
    (content: string, schema?: SchemaDisplay) => {
      const newMessage: ChatMessage = {
        id: getId(),
        type: 'ai',
        content,
        timestamp: new Date(),
        ...(schema !== undefined ? { schema } : {}),
      }
      addConversationMessages([newMessage])
    },
    []
  )

  // Add user message
  const addUserMessage = useCallback((content: string) => {
    const newMessage: ChatMessage = {
      id: getId(),
      type: 'user',
      content,
      timestamp: new Date(),
    }
    addConversationMessages([newMessage])
  }, [])

  const addStatusMessage = useCallback((type: StatusType, message: string) => {
    const newMessage: ChatMessage = {
      id: getId(),
      type: 'system',
      content: message,
      timestamp: new Date(),
      status: type,
    }
    addConversationMessages([newMessage])
  }, [])
  
  useEffect(() => {
    AIAssistantSocketClient.off('applicationCreationError').on('applicationCreationError', (responseData: any) => {
      if (responseData.error === 'Quota Ended') {
        addAIMessage('Sorry! The maximum quota for free users is finished. Try again tomorrow!')
      } else {
        addAIMessage('Sorry, something went wrong from our side. Do you want me to try again?')
      }
      setIsLoading(false);
    })

    AIAssistantSocketClient.off('applicationCreationSuccess').on('applicationCreationSuccess', (responseData: any) => {
      setIsLoading(false)

      const {
        data: { message, proposedSchema, appState: newAppState, statuses },
      } = responseData

      if (newAppState !== undefined) {
        dispatch({
          type: createApplicationAssistantActions.CHANGE_APP_STATE,
          payload: newAppState,
        })
      }

      statuses?.forEach(
        (status: { type: StatusType; action: Action; message: string }) => {
          addStatusMessage(status.type, status.message)
        }
      )

      setShowMessageChanges(null)
      addAIMessage(
        message,
        proposedSchema !== null
          ? {
              newTables: proposedSchema.newTables ?? [],
            }
          : undefined
      )
    });

    AIAssistantSocketClient.off('applicationCreationStatus').on('applicationCreationStatus', (statusObject: any) => {
      if (statusObject.status === 'finished') {
        if (statusObject.user !== null) {
          sessionStorage.setItem(
            `initial-application-user-${statusObject.appId}`,
            JSON.stringify(statusObject.user)
          )
        }

        setTimeout(() => {
          dispatch({
            type: createApplicationAssistantActions.RESET_STATE,
          })
        }, 3000)

        window.location.assign(
          `/apps/${statusObject.appId}/screens?showPreviewQRCode=true`
        )
      } else if (
        [
          'creating_database_failed',
          'creating_application_failed',
          'saving_database_failed',
          'creating_dummy_data_failed',
          'creating_screens_failed',
        ].includes(statusObject.status as ApplicationCreationStatus)
      ) {
        dispatch({
          type: createApplicationAssistantActions.CHANGE_BUILDING_APPLICATION_STATUS,
          payload: false,
        })
        const stepsMapping: Record<string, string> = {
          creating_database_failed: 'Creating Database',
          creating_application_failed: 'Creating Application',
          saving_database_failed: 'Saving Database',
          creating_dummy_data_failed: 'Creating Dummy Data',
          creating_screens_failed: 'Creating Screens',
        }
        addStatusMessage(
          'error',
          `Your application failed building`
        )
        addAIMessage(
          `Application Build Failed at the following step:\n${
            stepsMapping[statusObject.status]
          }`
        )
        if (statusObject.appId !== undefined) {
          addAIMessage(
            `The application was created partially.\n<a href="/apps/${statusObject.appId}/screens">Go to Partially Created Application</a>\nDo you want to create it with all the AI Steps Completed?`
          )
        }
      } else {
        dispatch({
          type: createApplicationAssistantActions.CHANGE_BUILDING_APPLICATION_STATUS,
          payload: statusObject.status,
        })
      }
    });

    if (AIAssistantSocketClient.connected !== true) {
      AIAssistantSocketClient.connect();
    }
  }, [addAIMessage, addStatusMessage]);

  const renderType = (fieldType: SchemaField['type']) => {
    if (typeof fieldType === 'string') {
      return fieldType
    }

    const relationshipMapping: Record<string, string> = {
      belongsTo: `${fieldType.tableName} (Belongs-To)`,
      hasMany: `${fieldType.tableName} (Has-Many)`,
      ManyToMany: `${fieldType.tableName} (Many-To-Many)`,
    }
    return relationshipMapping[fieldType.type]
  }

  const toggleSchemaChanges = (messageId: string) => {
    const newMessageId =
      showMessageChanges !== null && showMessageChanges === messageId
        ? null
        : messageId
    setShowMessageChanges(newMessageId)
  }

  const renderSchema = (message: ChatMessage) => {
    const { id: messageId, schema } = message

    if (!schema) return null
    if (schema.newTables.length === 0) return null

    const hasNewTables = schema.newTables && schema.newTables.length > 0

    const lastSchemaMessageId = [...(messages ?? [])]
      .reverse()
      .find(m => m.schema !== undefined && m.schema.newTables.length !== 0)?.id

    const isLastSchemaMessage = lastSchemaMessageId === messageId
    return (
      <>
        {showMessageChanges === message.id && (
          <>
            {hasNewTables && (
              <div className="ai-schema-section">
                <div className="ai-schema-section-title">
                  Collections to create
                </div>
                <div className="ai-schema-cards">
                  {schema.newTables!.map((table, i) => (
                    <div key={i} className="ai-schema-card">
                      <div className="ai-schema-card-header">
                        <span className="ai-schema-card-icon">📦</span>
                        <span className="ai-schema-card-name">
                          {table.name}
                        </span>
                      </div>
                      <div className="ai-schema-fields">
                        {table.fields.map((field, j) => (
                          <span key={j} className="ai-schema-field">
                            <span className="ai-schema-field-name">
                              {field.name}
                            </span>
                            <span className="ai-schema-field-type">
                              {renderType(field.type)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="ai-chat-choices spaced">
          {isLastSchemaMessage === true && (
            <button
              className="ai-chat-choice-btn"
              disabled={isLoading}
              onClick={() => {
                handleFlowResponse('Build the application for me')
              }}
            >
              Build Application
            </button>
          )}

          <button
            className="ai-chat-choice-btn"
            disabled={isLoading}
            onClick={() => {
              toggleSchemaChanges(message.id)
            }}
          >
            {showMessageChanges === message.id
              ? 'Hide Suggested Changes'
              : 'View Suggested Changes'}
          </button>
        </div>
      </>
    )
  }

  // Handle flow responses
  const handleFlowResponse = useCallback(
    async (response: string, isYes?: boolean) => {
      const userPrompt = response.trim()
      const messagesHistory =
        messages
          .filter(t => t.type !== 'system')
          .map(m => ({
            role: m.type === 'ai' ? 'assistant' : 'user',
            content: m.content,
            schema: m.schema,
          })) ?? []
      addUserMessage(userPrompt)
      setIsLoading(true)

      try {
        AIAssistantSocketClient.emit('applicationCreationRequest', {
          prompt: userPrompt,
          data: {
            messages: messagesHistory,
            appState,
            organizationId: getOrganizationId(),
          },
        })
      } catch (err: any) {
        setIsLoading(false)
        const errorMessage =
          err?.response?.status === 429 && err?.response?.data?.error
            ? err.response.data.error
            : 'Something went wrong. Please try again.'
        addAIMessage(errorMessage)
        console.error(err)
      }
    },
    [addAIMessage, addUserMessage, messages, store]
  )

  // Handle input submit
  const handleInputSubmit = () => {
    if (inputValue.trim()) {
      handleFlowResponse(inputValue)
      setInputValue('')
    }
  }

  const renderMessage = (message: ChatMessage) => {
    if (message.type === 'system') {
      if (message.status === undefined) {
        return (
          <div key={message.id} className="ai-chat-divider">
            <span>{message.content}</span>
          </div>
        )
      }

      return (
        <div
          key={message.id}
          className={`ai-chat-status status-${message.status}`}
        >
          {message.status === 'success' && (
            <i className="material-icons">check</i>
          )}
          {message.status === 'error' && (
            <i className="material-icons">error_outline</i>
          )}
          {message.status === 'info' && (
            <i className="material-icons">info_outline</i>
          )}
          <div>{message.content}</div>
        </div>
      )
    }

    const isSet = (variable: any) => {
      return variable !== null && variable !== undefined
    }

    return (
      <div key={message.id} className={`ai-chat-message ${message.type}`}>
        <div className="ai-chat-message-avatar">
          {message.type === 'ai' ? <AIIcon /> : <UserIcon />}
        </div>
        <div className="ai-chat-message-content">
          <div
            className="ai-chat-message-bubble"
            dangerouslySetInnerHTML={{
              __html: message.content
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/^#{1,6}\s*(.+)$/gm, (match, content) => {
                  const level = match.trim().indexOf(' ') // Count the number of hashes
                  return `<h${level}>${content.trim()}</h${level}>`
                })
                .replace(
                  /<dev>(.*?)<\/dev>/gs,
                  '<div class="ai-dev-note">$1</div>'
                )
                .replace(/\n/g, '<br />'),
            }}
          />
          {isSet(message.schema) && renderSchema(message)}
        </div>
      </div>
    )
  }

  const TODO_APP_PROMPT = `Create a multi-user app for managing TODO lists named SuperTODO. The user should be able to create and manage multiple lists. Each list is defined by a name, creation date, and completion date. Each list consists of one or more tasks. A task is defined by a name, description, and a status (completed or outstanding).

Users should be able to create lists, and then create tasks for specific lists. Both lists and tasks can be edited and deleted. Users should not be able to see or edit lists or tasks created by other users.`

  const MOBILE_CRM_PROMPT = `Create a basic customer relationship management app. The user should be able to manage companies and people. Each person should belong to a company, and companies can have many people. Each company should include a name, description, street address, city, state, and zip code. Each person should include a name, email address, phone number, and associated company. The app should be named SuperCRM.`

  const handleExamplePromptClick = (prompt: string) => {
    setInputValue(prompt)
    inputRef.current?.focus()
  }

  // Render suggestion UI
  const renderSuggestion = () => {
    return (
      <div className="ai-chat-suggestions">
        <div className="ai-chat-input-wrapper">
          <textarea
            ref={inputRef}
            className="ai-chat-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyUp={e =>
              e.shiftKey === false && e.key === 'Enter' && handleInputSubmit()
            }
            autoFocus
          />
          <button
            className="ai-chat-input-submit"
            onClick={handleInputSubmit}
            disabled={!inputValue.trim()}
          >
            <SendIcon />
          </button>
        </div>
        <div className="ai-chat-example-prompts">
          <span className="ai-chat-example-prompts-label">
            Want to try it? Use an example project prompt:
          </span>
          <div className="ai-chat-example-prompts-buttons">
            <button
              className="ai-chat-example-prompt-btn"
              onClick={() => handleExamplePromptClick(TODO_APP_PROMPT)}
              disabled={isLoading}
            >
              TODO App
            </button>
            <button
              className="ai-chat-example-prompt-btn"
              onClick={() => handleExamplePromptClick(MOBILE_CRM_PROMPT)}
              disabled={isLoading}
            >
              Mobile CRM
            </button>
          </div>
        </div>
      </div>
    )
  }

  const buildingMapping: Record<ApplicationCreationStatus, string> = {
    build_started: 'Build Started',
    creating_application: 'Creating Application',
    creating_database: 'Creating Database',
    creating_dummy_data: 'Creating Dummy Data',
    generating_images: 'Generating Images',
    creating_screens: 'Creating Screens',
    saving_database: 'Saving Database',
    creating_application_failed: 'Creating Application Failed',
    creating_database_failed: 'Creating Database Failed',
    creating_dummy_data_failed: 'Creating Dummy Data Failed',
    creating_screens_failed: 'Creating Screens Failed',
    saving_database_failed: 'Saving Database Failed',
    finished: 'Finished',
  }

  return (
    <div className="onboarding-ai-assistant">
      <div className={`ai-chat-panel ${isLoading ? 'is-loading' : ''}`}>
        <div className="ai-chat-messages">
          {messages.map(renderMessage)}

          {isLoading && (
            <div className="ai-chat-message ai">
              <div className="ai-chat-message-avatar">
                <AIIcon />
              </div>
              <div className="ai-chat-typing">
                {buildingApplicationStatus !== false && (
                  <div className="ai-build-status">
                    {
                      buildingMapping[
                        buildingApplicationStatus as ApplicationCreationStatus
                      ]
                    }
                  </div>
                )}
                <div className="ai-chat-typing-dot" />
                <div className="ai-chat-typing-dot" />
                <div className="ai-chat-typing-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {!isLoading && renderSuggestion()}
      </div>
    </div>
  )
}

export default InitialApplicationChatAssistant
